import {
  Box,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { create, remove } from '../../../api/ventura/location';
import { draftLocation, headCells, Location } from '../../../constants/locationTable';
import useAdGroupLocations from '../../../hooks/useAdGroupLocations';
import { useConfig } from '../../../hooks/useConfig';
import { useConstants } from '../../../hooks/useConstants';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';
import { useSnackBar } from '../../../hooks/useSnackBar';
import AutocompleteArray from '../../forms/AutocompleteArray';
import LocationForm from './LocationForm';
import LocationTableItem from './LocationTableItem';

function ExcludedLocations() {
  const {
    getValues,
    setValue,
    formState: { isSubmitSuccessful },
  } = useFormContext();

  // Need to compare the previous value of isSubmitSuccessful
  // The isSubmitSuccessful from useFormContext() may be triggered multiple times.
  // and useEffect() doesn't seem to catch if there was a change in value;
  const isSubmitSuccessfulPrevious = usePrevious(isSubmitSuccessful);

  const [adGroupId, adGroupName] = getValues(['id', 'name']);
  const { constants } = useConstants();

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const { showSnackBar } = useSnackBar();
  const [progress, setProgress] = useState(0);
  const [progressTitle, setProgressTitle] = useState('');

  const ACCEPTED_REGION_STATES = get(constants, 'CAMPAIGN.ACCEPTED_REGION_STATES', []).map((item) => item[1]);
  const ACCEPTED_REGION_DMAS = get(constants, 'CAMPAIGN.ACCEPTED_REGION_DMAS', []).map((item) => item[1]);

  const excludedLocationsForm = useForm({
    defaultValues: {
      excludedStates: [],
      excludedDMAs: [],
      excludedZipCodes: [],
    },
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isDirty: isExcludedLocationsFormDirty },
  } = excludedLocationsForm;

  useEffect(() => {
    setValue('isExcludedLocationsFormDirty', isExcludedLocationsFormDirty, { shouldDirty: true });
  }, [setValue, isExcludedLocationsFormDirty]);

  const { apiHost } = useConfig();
  const {
    data: excludedLocationsData,
    isFetchedAfterMount,
    refetch: refetchExcludedLocations,
    isLoading,
    isSuccess,
  } = useAdGroupLocations(adGroupId, true, '', 9999999);

  const handlePageChange = (_event: unknown, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [openExlude, setOpenExclude] = React.useState(false);

  // Auto-expand the excluded locations on first fetch if there are values.
  useEffect(() => {
    if (isFetchedAfterMount) {
      setOpenExclude(excludedLocationsData?.collection.length > 0);
    }
  }, [isFetchedAfterMount, excludedLocationsData]);

  const determineInvalidField = (model: Location) => {
    const { id, state, dma, zipcode } = model;

    // If there is an errors, there should be no ID assigned
    // and the field with the issue should be non-empty.
    if (!id) {
      if (state) {
        return 'excludedStates';
      }
      if (dma) {
        return 'excludedDMAs';
      }
      if (zipcode) {
        return 'excludedZipCodes';
      }
    }

    return undefined;
  };

  const saveExcludedLocation = useMutation({
    mutationFn: (payload: object) =>
      create(apiHost, adGroupId, {
        ...draftLocation,
        id: undefined,
        ...payload,
        excluded: true,
      }),
    onSuccess: (response) => {
      if (!isEmpty(response?.errors)) {
        setError(determineInvalidField(response.model), {
          type: 'manual',
          message: response.errors.join(' '),
        });
      }
    },
  });

  const deleteLocation = useMutation({
    mutationFn: (locationId: number) => remove(apiHost, locationId),
  });

  const filterExcludedLocation = (excludedLocationData, fieldName) =>
    excludedLocationData?.collection
      .filter((item) => item[fieldName] && item.name === item[fieldName])
      .map((excludedLocationItem) => excludedLocationItem[fieldName]) || [];

  const defaultExcludedStates = useMemo(
    () => filterExcludedLocation(excludedLocationsData, 'state'),
    [excludedLocationsData]
  );

  const defaultExcludedDMAs = useMemo(
    () => filterExcludedLocation(excludedLocationsData, 'dma'),
    [excludedLocationsData]
  );

  const defaultExcludedZipCodes = useMemo(
    () => filterExcludedLocation(excludedLocationsData, 'zipcode'),
    [excludedLocationsData]
  );

  const unpaginatedOtherExcludedLocation = excludedLocationsData?.collection.filter(
    (item) => item.name !== item.state && item.name !== item.dma && item.name !== item.zipcode
  );

  const paginatedOtherExcludedLocation = unpaginatedOtherExcludedLocation?.slice(
    page * perPage,
    page * perPage + perPage - 1
  );

  useEffect(() => {
    reset({
      excludedStates: defaultExcludedStates,
      excludedDMAs: defaultExcludedDMAs,
      excludedZipCodes: defaultExcludedZipCodes,
    });
  }, [reset, defaultExcludedStates, defaultExcludedDMAs, defaultExcludedZipCodes]);

  const [showProgress, hideProgress] = useModal(
    () => (
      <Stack px={10} pt={2} pb={5} alignItems="center" textAlign="center" spacing={2}>
        <Typography variant="h5">{progressTitle}</Typography>
        <Typography>Please stay on this page until the operation is 100% complete</Typography>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress variant="determinate" value={progress} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" component="div">
              {progress}%
            </Typography>
          </Box>
        </Box>
      </Stack>
    ),
    [progress, progressTitle],
    { escapeCloses: false }
  );

  const saveExcludedLocationHelper = async (
    title: string,
    fieldName: string,
    newExcludedLocations: string[],
    previousExcludedLocation: string[]
  ) => {
    const workload = [];
    for (let i = 0; i < newExcludedLocations.length; i += 1) {
      const value = newExcludedLocations[i];
      if (!previousExcludedLocation.includes(value)) {
        workload.push(() =>
          saveExcludedLocation.mutateAsync({
            name: value,
            [fieldName]: value, // Eg: `state: CA` or `dma: 1234` or `zipcode: 90210`
          })
        );
      }
    }

    // Delete excluded locations that are no longer in the new list.
    for (let i = 0; i < previousExcludedLocation.length; i += 1) {
      const value = previousExcludedLocation[i];
      if (!newExcludedLocations.includes(value)) {
        const id = excludedLocationsData?.collection.find((item) => item[fieldName] === value)?.id;
        if (id) {
          workload.push(() => deleteLocation.mutateAsync(id));
        }
      }
    }

    if (workload.length > 0) {
      setTimeout(() => showSnackBar('Please wait while the operation is in progress ...', 'success'), 1000);
      // Create an excluded locations that are not in the original list.
      setProgressTitle(title);

      setProgress(0);
      showProgress();
      for (let i = 0; i < workload.length; i += 1) {
        setProgress(Math.floor((i / workload.length) * 100));
        // eslint-disable-next-line no-await-in-loop
        await workload[i]();
      }
      setProgress(100);
      hideProgress();
    }

    return workload.length;
  };

  const save = handleSubmit(async (data) => {
    const { excludedStates, excludedDMAs, excludedZipCodes } = data;

    let totalOperations = 0;
    totalOperations += await saveExcludedLocationHelper(
      'Updating States',
      'state',
      excludedStates,
      defaultExcludedStates
    );
    totalOperations += await saveExcludedLocationHelper('Updating DMAs', 'dma', excludedDMAs, defaultExcludedDMAs);
    totalOperations += await saveExcludedLocationHelper(
      'Updating Zipcodes',
      'zipcode',
      excludedZipCodes,
      defaultExcludedZipCodes
    );

    refetchExcludedLocations();

    if (totalOperations > 0) {
      setTimeout(() => showSnackBar('The operation was successful', 'success'), 1000);
    }
  });

  useEffect(() => {
    if (isSubmitSuccessfulPrevious !== isSubmitSuccessful && isSubmitSuccessful) {
      save();
    }
  }, [save, isSubmitSuccessful, isSubmitSuccessfulPrevious]);

  const [excludedLocation, setExcludedLocation] = useState(null);
  const [showExcludedLocationForm, closeExcludedLocationForm] = useModal(
    () => (
      <LocationForm
        readOnly
        location={excludedLocation}
        adGroupId={adGroupId}
        adGroupName={adGroupName}
        onClose={closeExcludedLocationForm}
      />
    ),
    [excludedLocation, adGroupId, adGroupName]
  );

  return (
    <Box pt={2}>
      <FormControlLabel
        label={`Excluded Locations (${excludedLocationsData?.collection.length || 0})`}
        control={<Switch onChange={(e) => setOpenExclude(e.target.checked)} checked={openExlude} />}
      />
      <Collapse in={openExlude} timeout="auto" unmountOnExit={false} sx={{ mt: 3 }}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...excludedLocationsForm}>
          <Stack spacing={2}>
            <AutocompleteArray
              label="Excluded States"
              name="excludedStates"
              sx={{ width: '100%' }}
              options={ACCEPTED_REGION_STATES}
              autoHighlight
              limitTags={10}
            />
            <AutocompleteArray
              label="Excluded DMAs"
              name="excludedDMAs"
              sx={{ width: '100%' }}
              options={ACCEPTED_REGION_DMAS}
              autoHighlight
              limitTags={10}
            />
            <AutocompleteArray
              label="Excluded Zip Codes"
              name="excludedZipCodes"
              sx={{ width: '100%' }}
              options={[]}
              freeSolo
              limitTags={10}
            />
            {paginatedOtherExcludedLocation?.length > 0 && (
              <>
                <Typography sx={{ fontSize: '12px', color: '#616161' }}>
                  Other Excluded Locations ({unpaginatedOtherExcludedLocation?.length})
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {headCells.map((headCell) => (
                            <TableCell
                              key={headCell.id}
                              align={headCell.numeric ? 'right' : 'left'}
                              padding={headCell.id === 'name' ? 'none' : 'normal'}
                            >
                              <Typography>{headCell.label}</Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading &&
                          Array.from({ length: 5 }, () => Math.random()).map((rowItem) => (
                            <TableRow key={rowItem} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              {Array.from({ length: 15 }, () => Math.random()).map((colItem) => (
                                <TableCell key={colItem} scope="row">
                                  <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        {isSuccess &&
                          paginatedOtherExcludedLocation?.map((location) => (
                            <LocationTableItem
                              key={location.id}
                              location={location}
                              checked={undefined}
                              onSelectChange={undefined}
                              onClick={() => {
                                setExcludedLocation(location);
                                showExcludedLocationForm();
                              }}
                            />
                          ))}
                        {isSuccess && (
                          <TableRow key="locations-pagination">
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 500]}
                              count={unpaginatedOtherExcludedLocation?.length || 0}
                              page={page}
                              onPageChange={handlePageChange}
                              rowsPerPage={perPage}
                              onRowsPerPageChange={handlePerPageChange}
                            />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
          </Stack>
        </FormProvider>
      </Collapse>
    </Box>
  );
}

export default ExcludedLocations;
